import React, { useState, useMemo } from 'react';
import { Container, Spinner, Alert, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FaPlus } from 'react-icons/fa';
import { useProductos } from '../../hooks/useProductos';
import { ROUTES } from '../../constants';
import { useFiltros } from '../../hooks/useFiltros';
import { usePreciosMasivo } from '../../hooks/usePreciosMasivo';
import { obtenerValoresUnicos, verificarPreciosUniforme } from '../../utils/productUtils';
import { formatPrecio } from '../../utils/formatters';
import FiltrosProductos from '../../components/admin/FiltrosProductos';
import TablaProductos from '../../components/admin/TablaProductos';
import ModalActualizarPrecios from '../../components/admin/ModalActualizarPrecios';
import './Admin.css';

function AdminProductos() {
  const navigate = useNavigate();
  const { productos, loading, error, refetch } = useProductos();
  const {
    filtros,
    productosFiltrados,
    handleFiltroChange,
    limpiarFiltros,
    hayFiltrosActivos
  } = useFiltros(productos, ['modelo', 'categoria', 'material', 'color']);

  const [showModalPrecios, setShowModalPrecios] = useState(false);
  const { updating, error: precioError, actualizarPrecios } = usePreciosMasivo();

  // Obtener valores únicos para los selects de filtro
  const valoresUnicos = useMemo(() => {
    return {
      colores: obtenerValoresUnicos(productos, 'color'),
      materiales: obtenerValoresUnicos(productos, 'material'),
      categorias: obtenerValoresUnicos(productos, 'categoria')
    };
  }, [productos]);

  // Verificar si los precios son uniformes
  const preciosUniforme = useMemo(() => {
    return verificarPreciosUniforme(productosFiltrados);
  }, [productosFiltrados]);

  const handleAbrirModalPrecios = () => {
    setShowModalPrecios(true);
  };

  const handleCerrarModalPrecios = () => {
    setShowModalPrecios(false);
  };

  const handleActualizarPrecios = async (preciosForm) => {
    const ids = productosFiltrados.map(p => p.id);
    const result = await actualizarPrecios(ids, preciosForm);
    
    if (result.success) {
      await refetch();
      handleCerrarModalPrecios();
    }
  };

  return (
    <Container className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 className="admin-section-title mb-0">Productos</h5>
        <Button 
          variant="primary" 
          size="sm"
          onClick={() => navigate(`${ROUTES.ADMIN_PRODUCTOS}/nuevo`)}
          className="d-inline-flex align-items-center gap-2"
        >
          <FaPlus />
          Agregar Producto
        </Button>
      </div>

      {/* Filtros */}
      {!loading && !error && productos.length > 0 && (
        <FiltrosProductos
          filtros={filtros}
          onFiltroChange={handleFiltroChange}
          onLimpiar={limpiarFiltros}
          valoresUnicos={valoresUnicos}
        />
      )}

      {/* Botón para actualizar precios cuando hay filtros activos */}
      {!loading && !error && hayFiltrosActivos && productosFiltrados.length > 0 && (
        <div className="admin-form-container mb-3 p-3 d-flex justify-content-between align-items-center">
          <div>
            <strong>{productosFiltrados.length}</strong> producto{productosFiltrados.length !== 1 ? 's' : ''} encontrado{productosFiltrados.length !== 1 ? 's' : ''}
            {preciosUniforme && (
              <span className="text-muted ms-2">
                (Precios uniformes: Mayorista: {formatPrecio(preciosUniforme.precio)}, Minorista: {formatPrecio(preciosUniforme.preciomenor)})
              </span>
            )}
          </div>
          <Button
            variant="success"
            size="sm"
            onClick={handleAbrirModalPrecios}
            style={{ padding: '0.5rem 1.25rem', fontWeight: 500 }}
          >
            Actualizar Precios ({productosFiltrados.length})
          </Button>
        </div>
      )}

      {loading && <Spinner animation="border" size="sm" />}
      {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
      {precioError && <Alert variant="danger" className="mt-3">{precioError}</Alert>}

      {!loading && !error && productosFiltrados.length > 0 && (
        <TablaProductos productos={productosFiltrados} onEliminar={refetch} />
      )}

      {!loading && !error && productos.length === 0 && (
        <Alert variant="info" className="mt-3">No hay productos para mostrar</Alert>
      )}

      {!loading && !error && productos.length > 0 && productosFiltrados.length === 0 && (
        <Alert variant="warning" className="mt-3">
          No se encontraron productos que coincidan con los filtros aplicados.
        </Alert>
      )}

      {/* Modal para actualizar precios */}
      <ModalActualizarPrecios
        show={showModalPrecios}
        onHide={handleCerrarModalPrecios}
        productos={productosFiltrados}
        preciosUniforme={preciosUniforme}
        onActualizar={handleActualizarPrecios}
        updating={updating}
        error={precioError}
      />
    </Container>
  );
}

export default AdminProductos;
